import { useMemo } from 'react'
import { GeoJsonFillLayer, GeoJsonLineLayer } from '@wehunt/map'
import type { GeoJsonFeatureCollection, GeoJsonLineStringFeature } from '../../lib/mapGeoJson'
import type { GeoPt } from '../../lib/types'

/** Dim mask over everything outside the print frame. */
const DIM_FILL = 'rgba(0,0,0,0.45)'
/** The frame's white edge, 2 px wide. */
const FRAME_STROKE = '#ffffff'
const FRAME_WIDTH = 2
/** The 1 px-per-side dark casing beneath the white edge — same technique as
 *  `TeamMapLayers`' drawn-line casings (a wider stroke painted first,
 *  showing as a border once the narrower interior stroke paints on top): 1 px
 *  of casing on each side of the 2 px interior is a 4 px casing stroke. */
const CASING_STROKE = 'rgba(0,0,0,0.6)'
const CASING_INSET_PX = 1
const CASING_WIDTH = FRAME_WIDTH + 2 * CASING_INSET_PX

type GeoJsonPos = [number, number]

interface GeoJsonPolygonFeature {
  type: 'Feature'
  properties: Record<string, unknown>
  geometry: { type: 'Polygon'; coordinates: GeoJsonPos[][] }
}

const toPos = (p: GeoPt): GeoJsonPos => [p.lon, p.lat]

/** A ring closed for GeoJSON (first coordinate repeated at the end), mirroring
 *  `mapGeoJson.ts`'s own `closedRing` — not imported from there because that
 *  helper isn't exported, and this file's rings (a fixed world envelope, a
 *  4-corner frame) don't otherwise share anything with that module's
 *  hunting-ground geometry. */
function closedRing(ring: readonly GeoPt[]): GeoJsonPos[] {
  if (ring.length === 0) return []
  const coords = ring.map(toPos)
  const [fx, fy] = coords[0]
  const [lx, ly] = coords[coords.length - 1]
  return fx === lx && fy === ly ? coords : [...coords, [fx, fy]]
}

/**
 * The dim overlay's outer ring: the whole world, per Tanger's spec, which
 * lists it counter-clockwise as
 * `[-180,-85.05] → [180,-85.05] → [180,85.05] → [-180,85.05]`.
 *
 * `printFrameCorners` (see `printFrame.ts`) returns its four corners
 * counter-clockwise too, and this component uses them as-is for the frame's
 * own outline layers below. But MapLibre only renders a polygon's second ring
 * as a HOLE when it winds opposite to the first ring — same winding just
 * fills the hole back in solid. Rather than reverse the frame (and so draw
 * its outline backwards relative to every other consumer of
 * `printFrameCorners`), this world ring is listed in the REVERSE of Tanger's
 * order, i.e. clockwise, so it is the one ring in this file that doesn't
 * match its source order.
 */
const WORLD_RING_CW: readonly GeoPt[] = [
  { lon: -180, lat: 85.05 },
  { lon: 180, lat: 85.05 },
  { lon: 180, lat: -85.05 },
  { lon: -180, lat: -85.05 },
]

const EMPTY_POLYGONS: GeoJsonFeatureCollection<GeoJsonPolygonFeature> = { type: 'FeatureCollection', features: [] }
const EMPTY_LINES: GeoJsonFeatureCollection<GeoJsonLineStringFeature> = { type: 'FeatureCollection', features: [] }

function dimFeatureCollection(corners: GeoPt[] | null): GeoJsonFeatureCollection<GeoJsonPolygonFeature> {
  if (!corners) return EMPTY_POLYGONS
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [closedRing(WORLD_RING_CW), closedRing(corners)] },
    }],
  }
}

function frameFeatureCollection(corners: GeoPt[] | null): GeoJsonFeatureCollection<GeoJsonLineStringFeature> {
  if (!corners) return EMPTY_LINES
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: closedRing(corners) },
    }],
  }
}

export interface PrintFrameLayersProps {
  /** The print sheet's four corners (see `printFrameCorners`), or `null`
   *  when there is no server print frame for the current region/centre. */
  corners: GeoPt[] | null
}

/**
 * The print-preview overlay drawn on the live map: a dim mask over
 * everything outside the print frame, plus a white edge over a dark casing
 * on the frame itself.
 *
 * All three layers stay mounted even when `corners` is `null` (rendering
 * with empty data instead of unmounting) — the same reasoning as
 * `TeamMapLayers`' always-mounted `wash` layer: none of these layers pass a
 * `beforeId`, so paint order is mount order, and unmounting/remounting a
 * layer as the region flips between "has a frame" and "doesn't" would let it
 * land at a different point in the stack depending on which sibling effect
 * happened to run first.
 */
export function PrintFrameLayers({ corners }: PrintFrameLayersProps): JSX.Element {
  const dimFC = useMemo(() => dimFeatureCollection(corners), [corners])
  const frameFC = useMemo(() => frameFeatureCollection(corners), [corners])

  return (
    <>
      <GeoJsonFillLayer id="print-frame-dim" data={dimFC} paint={{ 'fill-color': DIM_FILL }} />
      <GeoJsonLineLayer
        id="print-frame-casing"
        data={frameFC}
        paint={{ 'line-color': CASING_STROKE, 'line-width': CASING_WIDTH }}
      />
      <GeoJsonLineLayer
        id="print-frame"
        data={frameFC}
        paint={{ 'line-color': FRAME_STROKE, 'line-width': FRAME_WIDTH }}
      />
    </>
  )
}
